import { Component, OnInit } from "@angular/core";
import * as moment from "moment";
import { MatDatepickerInputEvent } from "@angular/material";
import { NotificationsService } from "angular2-notifications";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";
import { ColDef, GridOptions, GridApi, ColumnApi } from "ag-grid-community";
import { UserService } from "../services/user.service";
import { OfframpService } from "../services/offramp.service";
import { TableUtil } from "../admintrademaster/tableUtil";

@Component({
  selector: "app-offramp-monitor",
  templateUrl: "./offramp-monitor.component.html",
  styleUrls: ["./offramp-monitor.component.css"],
})
export class OfframpMonitorComponent implements OnInit {
  _csrf: string;

  userList: any[] = [];
  filteredUserList: any[] = [];
  userSearchValue: string = "";
  isUserSelectOpen: boolean = false;

  filter: any = {
    fromDate: moment().subtract(6, "d").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
    userId: "",
  };

  // AG Grid - day-wise consolidated report
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  gridOptions: GridOptions;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
  };

  myFilter = (d: Date | null): boolean => true;

  constructor(
    private notifSer: NotificationsService,
    private userService: UserService,
    private offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) {
    this.initializeGrid();
  }

  ngOnInit() {
    this.csrf();
  }

  initializeGrid() {
    this.columnDefs = [
      {
        headerName: "Date",
        field: "date",
        valueFormatter: (params) => (params.value ? moment(params.value).format("DD-MMM-YYYY") : ""),
        flex: 1,
      },
      {
        headerName: "User",
        field: "user",
        valueGetter: () => this.getSelectedUserLabel(),
        flex: 1,
      },
      {
        headerName: "Total Transactions",
        field: "totalTransactions",
        valueFormatter: (params) => Number(params.value || 0).toLocaleString("en-US"),
        flex: 1,
      },
      {
        headerName: "Total Payout In (INR)",
        field: "totalPayoutIn",
        valueFormatter: (params) => this.formatAccounting(params.value),
        cellStyle: (params) => ({ color: params.value < 0 ? "#c62828" : "#212121" }),
        flex: 1.3,
      },
      {
        headerName: "Credits Added Today (INR)",
        field: "creditsAdded",
        valueFormatter: (params) => this.formatAccounting(params.value),
        cellStyle: (params) => ({ color: params.value < 0 ? "#c62828" : "#212121" }),
        flex: 1.3,
      },
      {
        headerName: "Running  Balance (INR)",
        field: "runningBalance",
        valueFormatter: (params) => this.formatAccounting(params.value),
        cellStyle: (params) => ({ color: params.value < 0 ? "#c62828" : "#212121" }),
        flex: 1.3,
      },
    ];

    this.gridOptions = {
      enableCellTextSelection: true,
      ensureDomOrder: true,
      columnDefs: this.columnDefs,
      defaultColDef: this.defaultColDef,
      rowData: this.rowData,
      pagination: false,
      suppressHorizontalScroll: false,
      enableRangeSelection: true,
      animateRows: true,
      rowHeight: 40,
      headerHeight: 40,
    };
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
  }

  csrf() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.loadUserList();
        this.getData();
      },
      (error) => {
        this.notifSer.error("Error", error.error.message, {
          timeOut: 3000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 50,
        });
      }
    );
  }

  loadUserList() {
    if (!this._csrf) {
      return;
    }

    const filter = {
      _csrf: this._csrf,
      perpage: 10000,
      page: 1,
      search: "",
      status: "Active",
    };

    this.offrampService.getOfframpCustomerList(filter).subscribe(
      (response: any) => {
        if (response && response.customers && Array.isArray(response.customers)) {
          this.userList = response.customers;
        } else if (response && Array.isArray(response)) {
          this.userList = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          this.userList = response.data;
        } else {
          this.userList = [];
        }
        this.filteredUserList = this.userList;
      },
      () => {
        this.userList = [];
        this.filteredUserList = [];
      }
    );
  }

  fromDateFilter(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      this.filter["fromDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  toDateFilter(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      this.filter["toDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  getData() {
    this.spinnerService.show();

    const payoutFilter = {
      _csrf: this._csrf,
      fromDate: this.filter.fromDate,
      toDate: this.filter.toDate,
      userId: this.filter.userId || "",
    };

    this.offrampService.getOfframpMonitorDailyReport(payoutFilter).subscribe(
      (report: any) => {
        this.rowData = Array.isArray(report) ? report : [];

        if (this.gridApi) {
          this.gridApi.setRowData(this.rowData);
        }

        this.spinnerService.hide();
      },
      () => {
        this.notifSer.error("Error", "Failed to load offramp monitor report", {
          timeOut: 3000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 50,
        });
        this.spinnerService.hide();
      }
    );
  }

  resetFilters() {
    this.filter = {
      fromDate: moment().subtract(6, "d").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      userId: "",
    };
    this.getData();
  }

  exportArray() {
    if (!this.rowData.length) {
      this.notifSer.info("Info", "No data available to export", {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: true,
        maxLength: 50,
      });
      return;
    }

    const exportData = this.rowData.map((row) => ({
      Date: row.date,
      User: this.getSelectedUserLabel(),
      "Total Transactions": row.totalTransactions,
      "Total Payout In (INR)": this.formatAccounting(row.totalPayoutIn),
      "Credits Added Today (INR)": this.formatAccounting(row.creditsAdded),
      "Running Balance (INR)": this.formatAccounting(row.runningBalance),
    }));
    TableUtil.exportArrayToExcel(exportData, "OfframpMonitorDailyReport");
  }

  getSelectedUserLabel(): string {
    if (!this.filter.userId) {
      return "All Users";
    }
    const user = this.userList.find((u) => u._id === this.filter.userId);
    return user ? `${user.name} (${user.email})` : this.filter.userId;
  }

  // Accounting-style formatting: 0/blank -> "-", negatives -> "(1,234.00)"
  formatAccounting(value: any): string {
    const num = value == null ? 0 : Number(value);
    if (!num) {
      return "-";
    }
    const formatted = Math.abs(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return num < 0 ? `(${formatted})` : formatted;
  }

  onUserSelectOpened(isOpen: boolean) {
    this.isUserSelectOpen = isOpen;
    if (isOpen) {
      this.userSearchValue = "";
      this.filteredUserList = this.userList;
      setTimeout(() => {
        this.addSearchToSelectPanel();
      }, 150);
    } else {
      this.userSearchValue = "";
      this.filteredUserList = this.userList;
    }
  }

  addSearchToSelectPanel() {
    const panel = document.querySelector(".user-select-panel");
    if (panel) {
      const existingSearch = panel.querySelector(".user-search-container");
      if (existingSearch) {
        existingSearch.remove();
      }

      const searchContainer = document.createElement("div");
      searchContainer.className = "user-search-container";
      searchContainer.innerHTML = `
        <input
          type="text"
          placeholder="Search user..."
          class="user-search-input"
          value="${this.userSearchValue || ""}">
      `;

      const searchInput = searchContainer.querySelector(".user-search-input") as HTMLInputElement;
      if (searchInput) {
        searchInput.addEventListener("input", (e: any) => {
          this.userSearchValue = e.target.value;
          this.filterUsers(e);
        });
        searchInput.addEventListener("click", (e: any) => {
          e.stopPropagation();
        });
        searchInput.addEventListener("keydown", (e: any) => {
          e.stopPropagation();
        });
      }

      if (panel.firstChild) {
        panel.insertBefore(searchContainer, panel.firstChild);
      } else {
        panel.appendChild(searchContainer);
      }

      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 50);
      }
    }
  }

  filterUsers(event: any) {
    const searchTerm = event ? (event.target ? event.target.value : event) : this.userSearchValue;
    this.userSearchValue = searchTerm || "";

    if (!searchTerm || searchTerm.trim() === "") {
      this.filteredUserList = this.userList;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredUserList = this.userList.filter((user) => {
      const name = (user.name || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/offramp-monitor/offramp-monitor.component.ts